import { useState, useEffect, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";
import Pagination from "@mui/material/Pagination";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import DataTable from "examples/Tables/DataTable";

import toast from "react-hot-toast";
import moment from "moment";
import { capitalCase } from "capital-case";

import { AccountContext } from "context/account";
import CategoryContext from "context/category-context";
import SharedContentContext from "context/shared-content";

import Prompt from "components/UpStyled/Prompt";
import api from "services/api";

import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import { Empty } from "antd";
import { compare } from "utils/helper";

const avatarSize = 26;

const SharedCollectionList = ({ size = 60, limit, tableOptions = {} }) => {
  const [collectionList, setCollectionList] = useState([]);
  const [filteredCollections, setFilteredCollections] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState({});

  const navigate = useNavigate();
  const owners = collectionList.filter((x) => x.store).map((x) => x.store);

  const account = useContext(AccountContext);
  const { resourceList, permissionMap } = useContext(SharedContentContext);

  const {
    tree,
    actions: { refreshCategoryTree, findNodeByID, findParents },
  } = useContext(CategoryContext);

  useEffect(() => {
    retrieveCollections();
  }, []);

  const retrieveCollections = async () => {
    try {
      const { data } = await api.collections.sharedList();

      let myCollections = data?.collections || [];

      myCollections = myCollections.sort((a, b) => compare(a, b, "-updated_at"));
      if (limit) {
        myCollections = myCollections.slice(0, limit);
      }

      setCollectionList(myCollections || []);
      setFilteredCollections(myCollections || []);
    } catch (err) {
      toast.error(`Retrieve collection list failed`);
      console.log(err);
    }
  };

  const getCollectionTableRow = (item) => {
    const collectionMeta = item?.metadata || {};

    let resource = resourceList.find((x) => x.resource_id == item.id) || {};
    return {
      key: item.id,
      moodboard: item?.moodboard?.[0]?.img,

      name: item.title,
      created_at: item.created_at,
      season: collectionMeta?.season,
      year: collectionMeta?.year,
      brand: collectionMeta?.brand,
      styles_number: item.products.length,
      updated_at: item.updated_at,
      shared_at: resource?.created_at,
      owner: item.store || {},
      ownerName: item?.store?.name,
    };
  };

  const columns = [
    {
      Header: "Moodboard",
      accessor: "moodboard",
      align: "center",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return (
          <Link to={`/shared-with-me/collections?id=${row?.original?.key}`}>
            <ImageCardWrapper data={value} mode={"normal"} height={size} width={size} iconMode />
          </Link>
        );
      },
    },
    {
      Header: "Name",
      accessor: "name",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return <div style={{ maxWidth: 400 }}>{value}</div>;
      },
    },

    {
      Header: "Season",
      accessor: "season",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return value?.label;
      },
    },

    {
      Header: "Number of Styles",
      accessor: "styles_number",
      isSorted: false,
      canSort: false,
    },
    {
      Header: "Updated Date",
      accessor: "updated_at",
      resizable: true,
      Cell: ({ row, value }) => {
        return (
          <Tooltip title={value ? moment(value).format("MM/DD/YY HH:mm:ss") : ""}>
            <div style={{ wordBreak: "break-word" }}>
              {value ? moment(value).format("MM/DD/YY") : ""}
            </div>
          </Tooltip>
        );
      },
    },
    {
      Header: "Shared Date",
      accessor: "shared_at",
      Cell: ({ row, value }) => {
        return (
          <Tooltip title={value ? moment(value).format("MM/DD/YY HH:mm:ss") : ""}>
            <div style={{ wordBreak: "break-word" }}>
              {value ? moment(value).format("MM/DD/YY") : ""}
            </div>
          </Tooltip>
        );
      },
    },
    {
      Header: "Owner",
      accessor: "ownerName",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return (
          <SuiBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="inherit"
            sx={{ cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              if (row.original?.owner?.id) {
                account?.id
                  ? navigate(`/wall/portfolio/${row.original?.owner?.id}`)
                  : navigate(`/public/portfolio/${row.original?.owner?.id}`);
              }
            }}
          >
            <Avatar
              src={row.original?.owner?.metadata?.avatar}
              sx={{ width: avatarSize, height: avatarSize, mr: 0.5 }}
            />
            {value}
          </SuiBox>
        );
      },
    },
  ];

  const collectionRows = filteredCollections.map((item) => getCollectionTableRow(item));

  const handleCollectionRowClick = (row) => {
    navigate(`/shared-with-me/collections?id=${row?.original?.key}`);
  };
  const handleFilterMoodboard = (owner) => {
    if (owner.id == selectedOwner.id) {
      setSelectedOwner({});
      setFilteredCollections(collectionList);
    } else {
      setSelectedOwner(owner);
      const newCollections = collectionList.filter((x) => x?.store?.id == owner?.id);
      setFilteredCollections(newCollections);
    }
  };

  const removeDuplicate = (storeList) => {
    let idSet = new Set();
    let filteredList = [];
    for (let i = 0; i < storeList.length; i++) {
      if (idSet.has(storeList[i].id)) {
        //
      } else {
        idSet.add(storeList[i].id);
        filteredList.push(storeList[i]);
      }
    }
    return filteredList;
  };

  const noDuplicateOwners = removeDuplicate(owners);

  const HeaderTool = (
    <SuiBox display="flex" alignItems="center" gap={0.5} overflow="auto">
      <SuiTypography sx={{ mr: 0.5, whiteSpace: "noWrap", minWidth: 100 }}>
        Filter by owner:
      </SuiTypography>
      {noDuplicateOwners.map((x) => {
        const storeInfo = x || {};
        return (
          <Tooltip title={storeInfo?.name}>
            <SuiBox
              borderRadius="50%"
              width={avatarSize + 8}
              height={avatarSize + 8}
              minWidth={avatarSize + 8}
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              border={({ borders: { borderWidth }, palette: { info } }) =>
                selectedOwner.id == x?.id ? `${borderWidth[1]} solid ${info.main}` : "none"
              }
              sx={{ cursor: "pointer" }}
            >
              <Avatar
                src={storeInfo?.metadata?.avatar}
                sx={{ width: avatarSize, height: avatarSize, cursor: "pointer" }}
                onClick={() => {
                  handleFilterMoodboard(x);
                }}
              />
            </SuiBox>
          </Tooltip>
        );
      })}
    </SuiBox>
  );

  return (
    <SuiBox>
      <SuiBox sx={{ overflow: "auto" }}>
        {collectionRows.length > 0 ? (
          <DataTable
            table={{ columns: columns, rows: collectionRows }}
            entriesPerPage={{ defaultValue: 10 }}
            showTotalEntries={true}
            pagination={true}
            canSearch
            handleRowClick={handleCollectionRowClick}
            HeaderTool={HeaderTool}
            {...tableOptions}
          />
        ) : (
          <Empty description={false} />
        )}
      </SuiBox>
    </SuiBox>
  );
};

export default SharedCollectionList;
